import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, CalendarCheck } from "lucide-react";
import { CALENDLY_URL } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const WELCOME =
  "Bonjour ! Je suis l'assistant Syndicappli. Posez-moi vos questions sur nos fonctionnalités, nos offres, ou réservez directement une démo de 30 minutes.";

const renderMd = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

const QUICK = [
  { id: "offres", label: "Vos offres ?" },
  { id: "demo", label: "Réserver une démo" },
  { id: "ag", label: "Comment se passe une AG en visio ?" },
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(
    `web-${Math.random().toString(36).slice(2, 10)}${Date.now()}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const patchLast = (content) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content };
      return copy;
    });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: msg },
      { role: "assistant", content: "" },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/${sessionRef.current}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { delta, error } = JSON.parse(payload);
            if (delta) patchLastContent(delta);
            if (error) patchLastContent(error);
          } catch (_) {}
        }
      }
    } catch (e) {
      patchLast("Connexion interrompue. Réessayez dans un instant.");
    } finally {
      setStreaming(false);
    }
  };

  const patchLastContent = (delta) =>
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { role: "assistant", content: (last.content || "") + delta };
      return copy;
    });

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fermer l'assistant" : "Ouvrir l'assistant"}
        data-testid="chat-open-button"
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white shadow-[0_20px_45px_-12px_rgba(0,190,200,0.7)] transition-transform duration-300 hover:scale-110 active:scale-95"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "x" : "chat"}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {open ? <X size={24} /> : <MessageCircle size={24} />}
          </motion.span>
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="fixed bottom-24 left-6 z-50 flex h-[520px] w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-3xl border border-black/5 bg-white shadow-[0_40px_90px_-25px_rgba(0,55,70,0.5)] sm:w-[380px]"
            data-testid="chat-panel"
          >
            {/* En-tête dégradé */}
            <div className="flex items-center gap-3 bg-[linear-gradient(115deg,#2BC97E,#00C4CC)] px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <MessageCircle size={17} className="text-white" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Assistant Syndicappli</p>
                <p className="flex items-center gap-1.5 text-[10px] font-semibold text-white/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  En ligne — propulsé par ChatGPT
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer l'assistant"
                data-testid="chat-close-button"
                className="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-mist p-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {m.role === "user" ? (
                    <p
                      className="max-w-[80%] rounded-2xl rounded-tr-md bg-bubble px-4 py-2.5 text-[13px] leading-snug text-ink"
                      data-testid="chat-message-user"
                    >
                      {m.content}
                    </p>
                  ) : m.content ? (
                    <p
                      className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-md border border-black/5 bg-white px-4 py-2.5 text-[13px] leading-snug text-ink shadow-sm"
                      data-testid="chat-message-assistant"
                    >
                      {renderMd(m.content)}
                    </p>
                  ) : (
                    <span className="flex items-center gap-1 rounded-2xl rounded-tl-md border border-black/5 bg-white px-4 py-3 shadow-sm" data-testid="chat-typing-indicator">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink/40" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink/40 [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink/40 [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK.map((q) =>
                    q.id === "demo" ? (
                      <a
                        key={q.id}
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`chat-quick-${q.id}`}
                        className="flex items-center gap-1.5 rounded-full border border-electric/30 bg-white px-3.5 py-2 text-[12px] font-semibold text-electric transition-colors hover:bg-mist"
                      >
                        <CalendarCheck size={13} />
                        {q.label}
                      </a>
                    ) : (
                      <button
                        key={q.id}
                        onClick={() => send(q.label)}
                        data-testid={`chat-quick-${q.id}`}
                        className="rounded-full border border-electric/30 bg-white px-3.5 py-2 text-[12px] font-semibold text-electric transition-colors hover:bg-mist"
                      >
                        {q.label}
                      </button>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Saisie */}
            <div className="flex items-center gap-2 border-t border-black/5 bg-white p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrire ici..."
                maxLength={1000}
                className="input-app h-11 flex-1 !rounded-full"
                data-testid="chat-input"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                aria-label="Envoyer le message"
                data-testid="chat-send-button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white transition-transform duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
